import React from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@wove/web-ui'
import { X } from 'lucide-react'
import {
  CreateCustomFieldDefinition,
  UpdateCustomFieldDefinition,
  CustomFieldDefinition,
  FIELD_TYPE_LABELS,
  APPLIES_TO_LABELS,
  SUPPLIED_BY_LABELS,
} from '@wove/api'
import {
  CustomFieldAppliesTo,
  CustomFieldSuppliedBy,
  CustomFieldType,
} from '@wove/common/customFields'

const customFieldSchema = z.object({
  fieldKey: z
    .string()
    .min(1, 'Field key is required')
    .max(50, 'Field key must be 50 characters or less')
    .regex(
      /^[a-zA-Z][a-zA-Z0-9_]*$/,
      'Field key must start with a letter and contain only letters, numbers, and underscores',
    ),
  fieldName: z
    .string()
    .min(1, 'Field name is required')
    .max(100, 'Field name must be 100 characters or less'),
  fieldType: z.nativeEnum(CustomFieldType),
  fieldOrder: z.number().min(0, 'Field order must be a positive number'),
  appliesTo: z.nativeEnum(CustomFieldAppliesTo),
  suppliedBy: z.nativeEnum(CustomFieldSuppliedBy),
  isAiExtractable: z.boolean(),
  aiDescription: z.string().max(500, 'AI description must be 500 characters or less').optional(),
  userDescription: z
    .string()
    .max(500, 'User description must be 500 characters or less')
    .optional(),
  customerDescription: z
    .string()
    .max(500, 'Customer description must be 500 characters or less')
    .optional(),
})

type CustomFieldFormData = z.infer<typeof customFieldSchema>

interface CustomFieldFormProps {
  field?: CustomFieldDefinition
  onSubmit: (data: CreateCustomFieldDefinition | UpdateCustomFieldDefinition) => Promise<void>
  onCancel: () => void
  isSubmitting?: boolean
}

export default function CustomFieldForm({
  field,
  onSubmit,
  onCancel,
  isSubmitting,
}: CustomFieldFormProps) {
  const isEditing = !!field

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CustomFieldFormData>({
    resolver: zodResolver(customFieldSchema),
    defaultValues: field
      ? {
          fieldKey: field.fieldKey,
          fieldName: field.fieldName,
          fieldType: field.fieldType,
          fieldOrder: field.fieldOrder,
          appliesTo: field.appliesTo,
          suppliedBy: field.suppliedBy,
          isAiExtractable: field.isAiExtractable,
          aiDescription: field.aiDescription || '',
          userDescription: field.userDescription || '',
          customerDescription: field.customerDescription || '',
        }
      : {
          fieldType: CustomFieldType.TEXT,
          appliesTo: CustomFieldAppliesTo.RFQ,
          suppliedBy: CustomFieldSuppliedBy.CUSTOMER,
          isAiExtractable: false,
          fieldOrder: 1,
        },
  })

  const isAiExtractable = watch('isAiExtractable')

  const onSubmitForm = async (data: CustomFieldFormData) => {
    await onSubmit(data)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">
            {isEditing ? 'Edit Custom Field' : 'Create Custom Field'}
          </h2>
          <Button variant="ghost" size="icon" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit(onSubmitForm)} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Field Key *</label>
              <input
                {...register('fieldKey')}
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., shipment_priority"
                disabled={isEditing} // Don't allow editing field key
              />
              {errors.fieldKey && (
                <p className="text-red-500 text-sm mt-1">{errors.fieldKey.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Field Name *</label>
              <input
                {...register('fieldName')}
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Shipment Priority"
              />
              {errors.fieldName && (
                <p className="text-red-500 text-sm mt-1">{errors.fieldName.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Field Type *</label>
              <select
                {...register('fieldType')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Object.entries(FIELD_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              {errors.fieldType && (
                <p className="text-red-500 text-sm mt-1">{errors.fieldType.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Field Order *</label>
              <input
                {...register('fieldOrder', { valueAsNumber: true })}
                type="number"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="1"
              />
              {errors.fieldOrder && (
                <p className="text-red-500 text-sm mt-1">{errors.fieldOrder.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Applies To *</label>
              <select
                {...register('appliesTo')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Object.entries(APPLIES_TO_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              {errors.appliesTo && (
                <p className="text-red-500 text-sm mt-1">{errors.appliesTo.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Supplied By *</label>
              <select
                {...register('suppliedBy')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Object.entries(SUPPLIED_BY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              {errors.suppliedBy && (
                <p className="text-red-500 text-sm mt-1">{errors.suppliedBy.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="flex items-center space-x-2">
              <input
                {...register('isAiExtractable')}
                type="checkbox"
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">AI Extractable</span>
            </label>
            <p className="text-xs text-gray-500 mt-1">
              Enable AI to automatically extract this field from documents
            </p>
          </div>

          {isAiExtractable && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                AI Description *
              </label>
              <textarea
                {...register('aiDescription')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Describe this field for AI extraction (e.g., 'The priority level of the shipment, usually High, Medium, or Low')"
              />
              {errors.aiDescription && (
                <p className="text-red-500 text-sm mt-1">{errors.aiDescription.message}</p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">User Description</label>
            <textarea
              {...register('userDescription')}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Description shown to internal users"
            />
            {errors.userDescription && (
              <p className="text-red-500 text-sm mt-1">{errors.userDescription.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer Description
            </label>
            <textarea
              {...register('customerDescription')}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Description shown to customers"
            />
            {errors.customerDescription && (
              <p className="text-red-500 text-sm mt-1">{errors.customerDescription.message}</p>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : isEditing ? 'Update Field' : 'Create Field'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
